import { useEffect, useState } from "react";
import { Building2, Mail, Send, RotateCw, X, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppHeader } from "@/components/AppHeader";
import { CompanyProfileCard } from "@/components/CompanyProfileCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { tr, locale } from "@/lib/i18n";
const TH = ({ children }) => <th className="px-4 py-3 text-left text-xs font-tech uppercase tracking-wider text-slate-500">{children}</th>;
const fmtDate = (d) => new Date(d).toLocaleDateString(locale());

const RoleSelect = ({ value, onChange, disabled, testid }) => (
  <Select value={value} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger className="h-9 w-44" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent><SelectItem value="member">{tr("Membro")}</SelectItem><SelectItem value="admin">{tr("Admin azienda")}</SelectItem></SelectContent>
  </Select>
);

const InviteForm = ({ onSent }) => {
  const [f, setF] = useState({ email: "", role: "member" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/company/invites", f); setF({ email: "", role: "member" }); onSent(data); }
    catch (ex) { toast.error(apiError(ex)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap gap-2">
      <Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="email@collega.ch" className="h-10 flex-1 min-w-[220px]" data-testid="invite-email-input" />
      <RoleSelect value={f.role} onChange={(v) => setF({ ...f, role: v })} testid="invite-role-select" />
      <Button type="submit" disabled={busy} className="h-10 bg-[#0F52BA] hover:bg-[#0D47A1]" data-testid="invite-send-btn"><Send className="w-4 h-4 mr-1.5" />{tr("Invia invito")}</Button>
    </form>
  );
};

const sentToast = (d) => {
  if (d.email_sent) return toast.success(tr("Invito inviato a {0}", [d.email]));
  toast.warning(tr("Email non inviata: copia il link e invialo tu"), { duration: 10000, action: { label: tr("Copia link"), onClick: () => navigator.clipboard?.writeText(d.link) } });
};

const Invites = ({ invites, reload }) => {
  const resend = async (i) => { try { sentToast((await api.post(`/company/invites/${i.id}/resend`)).data); reload(); } catch (ex) { toast.error(apiError(ex)); } };
  const cancel = async (i) => { try { await api.delete(`/company/invites/${i.id}`); toast.success(tr("Invito annullato")); reload(); } catch (ex) { toast.error(apiError(ex)); } };
  if (!invites.length) return <div className="p-6 text-sm text-slate-400" data-testid="invites-empty">{tr("Nessun invito in sospeso.")}</div>;
  return (
    <table className="w-full text-sm" data-testid="invites-table">
      <thead className="border-b border-slate-200"><tr><TH>{tr("Email")}</TH><TH>{tr("Ruolo")}</TH><TH>{tr("Invitato da")}</TH><TH>{tr("Scadenza")}</TH><TH /></tr></thead>
      <tbody>{invites.map((i) => (
        <tr key={i.id} className="border-b border-slate-100" data-testid={`invite-row-${i.id}`}>
          <td className="px-4 py-3 font-medium"><Mail className="w-3.5 h-3.5 inline mr-1.5 text-slate-400" />{i.email}</td>
          <td className="px-4 py-3">{i.role === "admin" ? tr("Admin azienda") : tr("Membro")}</td>
          <td className="px-4 py-3 text-slate-500">{i.invited_by}</td>
          <td className="px-4 py-3">{i.expired ? <span className="text-amber-700" data-testid={`invite-expired-${i.id}`}>{tr("Scaduto")}</span> : fmtDate(i.expires_at)}</td>
          <td className="px-4 py-3 text-right whitespace-nowrap">
            <Button variant="ghost" size="sm" onClick={() => resend(i)} data-testid={`invite-resend-${i.id}`}><RotateCw className="w-4 h-4 mr-1" />{tr("Rinvia")}</Button>
            <Button variant="ghost" size="sm" onClick={() => cancel(i)} className="hover:text-amber-700" data-testid={`invite-cancel-${i.id}`}><X className="w-4 h-4 mr-1" />{tr("Annulla")}</Button>
          </td>
        </tr>))}</tbody>
    </table>
  );
};

const MemberRow = ({ m, me, admin, patch, onDel }) => {
  const self = m.id === me;
  return (
    <tr className="border-b border-slate-100" data-testid={`member-row-${m.id}`}>
      <td className="px-4 py-3"><div className="font-medium text-slate-900">{m.name}{self && <span className="text-xs text-slate-400"> {tr("(tu)")}</span>}</div><div className="text-xs text-slate-500">{m.email}</div></td>
      <td className="px-4 py-3">{admin ? <RoleSelect value={m.group_role || "member"} disabled={self} onChange={(v) => patch(m, { group_role: v })} testid={`member-role-${m.id}`} />
        : <span data-testid={`member-role-${m.id}`}>{m.group_role === "admin" ? tr("Admin azienda") : tr("Membro")}</span>}</td>
      <td className="px-4 py-3 font-tech">{m.projects}</td>
      <td className="px-4 py-3">{admin ? <Switch checked={m.active} disabled={self} onCheckedChange={(v) => patch(m, { active: v })} data-testid={`member-active-${m.id}`} />
        : (m.active ? tr("Sì") : "No")}</td>
      {admin && <td className="px-4 py-3 text-right"><Button variant="ghost" size="icon" title={tr("Rimuovi")} disabled={self} onClick={() => onDel(m)} className="hover:text-amber-700" data-testid={`member-delete-${m.id}`}><Trash2 className="w-4 h-4" /></Button></td>}
    </tr>
  );
};

export default function Company() {
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [del, setDel] = useState(null);
  const [last, setLast] = useState(null);
  const load = () => api.get("/company").then(({ data }) => setC(data)).catch((ex) => setC({ error: apiError(ex) }));
  useEffect(() => { load(); }, []);
  const patch = async (m, body) => { try { await api.put(`/company/members/${m.id}`, body); toast.success(tr("Utente aggiornato")); load(); } catch (ex) { toast.error(apiError(ex)); } };
  const remove = async () => { try { await api.delete(`/company/members/${del.id}`); toast.success(tr("Utente rimosso")); load(); } catch (ex) { toast.error(apiError(ex)); } setDel(null); };
  if (!c) return <div className="min-h-screen bg-slate-50"><AppHeader /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-10 space-y-6">
        <div className="sr-rise">
          <div className="text-xs font-tech uppercase tracking-wider text-[#0F52BA] mb-2">{tr("La mia azienda")}</div>
          <h1 className="text-4xl sm:text-5xl font-display font-bold tracking-tight text-slate-900" data-testid="company-name">{c.name || tr("Nessuna azienda")}</h1>
          <p className="text-slate-500 mt-2" data-testid="company-summary">{c.error || `${c.members.length} ${c.members.length === 1 ? tr("membro") : tr("membri")} · ${c.projects} ${c.projects === 1 ? tr("progetto condiviso") : tr("progetti condivisi")}`}</p>
        </div>
        {c.profile && <CompanyProfileCard key={JSON.stringify(c.profile)} c={c} reload={load} />}
        {c.is_admin && (
          <section className="bg-white border border-slate-200 rounded-lg sr-rise" style={{ animationDelay: "60ms" }} data-testid="company-invite-card">
            <div className="px-5 py-4 border-b border-slate-200"><h2 className="font-display font-semibold">{tr("Invita un collega")}</h2>
              <p className="text-sm text-slate-500">{tr("Riceverà un'email con un link valido 7 giorni per creare il suo account.")}</p></div>
            <div className="p-5 space-y-3">
              <InviteForm onSent={(d) => { sentToast(d); setLast(d.email_sent ? null : d); load(); }} />
              {last && (
                <div className="flex gap-2 items-center text-xs" data-testid="invite-fallback-link">
                  <code className="flex-1 break-all bg-slate-50 border rounded px-2 py-1.5 font-tech">{last.link}</code>
                  <Button size="icon" variant="outline" onClick={() => navigator.clipboard?.writeText(last.link)}><Copy className="w-4 h-4" /></Button>
                </div>
              )}
            </div>
            <div className="border-t border-slate-200"><Invites invites={c.invites} reload={load} /></div>
          </section>
        )}
        {c.members && (
          <section className="bg-white border border-slate-200 rounded-lg overflow-x-auto sr-rise" style={{ animationDelay: "120ms" }}>
            <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2"><Building2 className="w-4 h-4 text-[#0F52BA]" /><h2 className="font-display font-semibold">{tr("Membri")}</h2></div>
            <table className="w-full text-sm" data-testid="members-table">
              <thead className="border-b border-slate-200"><tr><TH>{tr("Utente")}</TH><TH>{tr("Ruolo")}</TH><TH>{tr("Progetti")}</TH><TH>{tr("Attivo")}</TH>{c.is_admin && <TH />}</tr></thead>
              <tbody>{c.members.map((m) => <MemberRow key={m.id} m={m} me={user.id} admin={c.is_admin} patch={patch} onDel={setDel} />)}</tbody>
            </table>
          </section>
        )}
      </main>
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent data-testid="member-delete-dialog">
          <AlertDialogHeader><AlertDialogTitle>{tr("Rimuovere")} {del?.name}?</AlertDialogTitle>
            <AlertDialogDescription>{tr("L'account viene eliminato e le sue API key revocate. I suoi progetti restano all'azienda e passano a te.")}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="member-delete-cancel">{tr("Annulla")}</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-amber-600 hover:bg-amber-700" data-testid="member-delete-confirm">{tr("Rimuovi")}</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
